export const WEBINAR_FALLBACK_IMAGE = '/images/webinars/webinars-hero-desk.png';
export const SPEAKER_FALLBACK_IMAGE = '/images/companies/company-road-truck.png';

export function sanitizeText(value) {
  return String(value || '').trim();
}

export function slugifyPublic(value, fallback = 'item') {
  const slug = sanitizeText(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
  return slug || fallback;
}

export function getInitials(name) {
  return sanitizeText(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'MG';
}

export function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value._seconds === 'number') return value._seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatMoney(amount, currency = 'USD') {
  const parsed = Number(amount || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return 'Free';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: sanitizeText(currency || 'USD').toUpperCase(),
      minimumFractionDigits: parsed % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(parsed);
  } catch {
    return `${sanitizeText(currency || 'USD').toUpperCase()} ${parsed.toFixed(2)}`;
  }
}

export function formatWebinarSchedule(webinar) {
  const value = webinar?.startAt || webinar?.startsAt || webinar?.date || webinar?.scheduledAt || webinar?.eventDate;
  const millis = timestampToMillis(value);
  if (millis) {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(millis));
  }
  return sanitizeText(webinar?.scheduleLabel || webinar?.time || webinar?.dateTime) || 'Schedule to be announced';
}

export function getWebinarPrice(webinar) {
  return Number(webinar?.paymentAmount ?? webinar?.priceAmount ?? webinar?.amount ?? webinar?.price ?? 0) || 0;
}

export function isPublishedWebinar(webinar) {
  return ['scheduled', 'registration_open', 'full', 'live'].includes(sanitizeText(webinar?.status || 'registration_open'));
}

export function getWebinarSlug(webinar, fallback = 'webinar') {
  return slugifyPublic(webinar?.slug || webinar?.id || webinar?.title, fallback);
}

export function getWebinarDetailPath(webinar) {
  return `/webinars/${getWebinarSlug(webinar)}`;
}

export function getSpeakerProfile(webinar) {
  const speaker = webinar?.speaker || {};
  const name = sanitizeText(speaker.displayName || speaker.name || webinar?.speakerName);
  const profileSlug = slugifyPublic(speaker.profileSlug || webinar?.speakerProfileSlug || name, name ? 'speaker' : '');
  const profile = {
    id: sanitizeText(speaker.id || webinar?.speakerId),
    displayName: name,
    name,
    title: sanitizeText(speaker.title || webinar?.speakerTitle),
    company: sanitizeText(speaker.company || webinar?.speakerCompany),
    headline: sanitizeText(speaker.headline),
    shortBio: sanitizeText(speaker.shortBio),
    fullBio: sanitizeText(speaker.fullBio),
    bio: sanitizeText(speaker.shortBio || speaker.bio || speaker.fullBio || webinar?.speakerBio),
    headshotUrl: sanitizeText(speaker.headshotUrl || webinar?.speakerHeadshotUrl),
    portraitAlt: sanitizeText(speaker.portraitAlt),
    profileSlug,
    location: sanitizeText(speaker.location || webinar?.speakerLocation),
    linkedInUrl: sanitizeText(speaker.linkedInUrl || webinar?.speakerLinkedInUrl),
    websiteUrl: sanitizeText(speaker.websiteUrl),
    quote: sanitizeText(speaker.quote),
    previousExperience: Array.isArray(speaker.previousExperience) ? speaker.previousExperience.filter(Boolean) : [],
    workHistory: Array.isArray(speaker.workHistory) ? speaker.workHistory.filter(Boolean) : [],
    credentials: Array.isArray(speaker.credentials) ? speaker.credentials.filter(Boolean) : [],
    specialties: Array.isArray(speaker.specialties) ? speaker.specialties.filter(Boolean) : [],
    trainingFocus: Array.isArray(speaker.trainingFocus) ? speaker.trainingFocus.filter(Boolean) : [],
    languages: Array.isArray(speaker.languages) ? speaker.languages.filter(Boolean) : [],
    featured: speaker.featured === true,
    sortOrder: Number.isFinite(Number(speaker.sortOrder)) ? Number(speaker.sortOrder) : 0,
    webinars: [],
  };
  if (!profile.name && !profile.headshotUrl && !profile.bio) return null;
  return profile;
}

export function normalizeSpeakerProfileRecord(speaker = {}) {
  const name = sanitizeText(speaker.displayName || speaker.name);
  const profileSlug = slugifyPublic(speaker.profileSlug || name, name ? 'speaker' : '');
  const profile = {
    id: sanitizeText(speaker.id),
    displayName: name,
    name,
    title: sanitizeText(speaker.title),
    company: sanitizeText(speaker.company),
    headline: sanitizeText(speaker.headline),
    shortBio: sanitizeText(speaker.shortBio),
    fullBio: sanitizeText(speaker.fullBio),
    bio: sanitizeText(speaker.shortBio || speaker.bio || speaker.fullBio),
    headshotUrl: sanitizeText(speaker.headshotUrl),
    portraitAlt: sanitizeText(speaker.portraitAlt),
    profileSlug,
    location: sanitizeText(speaker.location),
    linkedInUrl: sanitizeText(speaker.linkedInUrl),
    websiteUrl: sanitizeText(speaker.websiteUrl),
    quote: sanitizeText(speaker.quote),
    previousExperience: Array.isArray(speaker.previousExperience) ? speaker.previousExperience.filter(Boolean) : [],
    workHistory: Array.isArray(speaker.workHistory) ? speaker.workHistory.filter(Boolean) : [],
    credentials: Array.isArray(speaker.credentials) ? speaker.credentials.filter(Boolean) : [],
    specialties: Array.isArray(speaker.specialties) ? speaker.specialties.filter(Boolean) : [],
    trainingFocus: Array.isArray(speaker.trainingFocus) ? speaker.trainingFocus.filter(Boolean) : [],
    languages: Array.isArray(speaker.languages) ? speaker.languages.filter(Boolean) : [],
    featured: speaker.featured === true,
    sortOrder: Number.isFinite(Number(speaker.sortOrder)) ? Number(speaker.sortOrder) : 0,
    webinars: Array.isArray(speaker.webinars) ? speaker.webinars : [],
  };
  if (!profile.name && !profile.headshotUrl && !profile.bio) return null;
  return profile;
}

function mergeSpeakerProfile(existing = {}, incoming = {}) {
  const existingWebinars = Array.isArray(existing.webinars) ? existing.webinars : [];
  const incomingWebinars = Array.isArray(incoming.webinars) ? incoming.webinars : [];
  const merged = { ...existing };
  Object.entries(incoming).forEach(([key, value]) => {
    if (key === 'webinars') return;
    if (Array.isArray(value)) {
      if (value.length > 0 && (!Array.isArray(merged[key]) || merged[key].length === 0)) {
        merged[key] = value;
      }
      return;
    }
    if (value && !merged[key]) {
      merged[key] = value;
    }
  });
  return {
    ...merged,
    webinars: [...existingWebinars, ...incomingWebinars],
  };
}

export function buildSpeakerProfiles(webinars = []) {
  const speakers = new Map();
  webinars.forEach((webinar) => {
    const profile = getSpeakerProfile(webinar);
    if (!profile) return;
    const key = profile.profileSlug || slugifyPublic(profile.name || profile.headshotUrl, 'speaker');
    speakers.set(key, mergeSpeakerProfile(speakers.get(key) || {}, {
      ...profile,
      webinars: [webinar],
    }));
  });
  return Array.from(speakers.values()).sort((a, b) => {
    const featuredDelta = Number(b.featured === true) - Number(a.featured === true);
    if (featuredDelta !== 0) return featuredDelta;
    const sortDelta = (Number(a.sortOrder) || Number.MAX_SAFE_INTEGER) - (Number(b.sortOrder) || Number.MAX_SAFE_INTEGER);
    if (sortDelta !== 0) return sortDelta;
    return sanitizeText(a.name).localeCompare(sanitizeText(b.name));
  });
}

export function buildSpeakerProfilesFromSources(speakerRecords = [], webinars = []) {
  const speakers = new Map();
  speakerRecords.forEach((speaker) => {
    const profile = normalizeSpeakerProfileRecord(speaker);
    if (!profile) return;
    const key = profile.profileSlug || slugifyPublic(profile.name || profile.headshotUrl, 'speaker');
    speakers.set(key, mergeSpeakerProfile(speakers.get(key) || {}, profile));
  });
  buildSpeakerProfiles(webinars).forEach((speaker) => {
    const key = speaker.profileSlug || slugifyPublic(speaker.name || speaker.headshotUrl, 'speaker');
    speakers.set(key, mergeSpeakerProfile(speakers.get(key) || {}, speaker));
  });
  return Array.from(speakers.values()).sort((a, b) => {
    const featuredDelta = Number(b.featured === true) - Number(a.featured === true);
    if (featuredDelta !== 0) return featuredDelta;
    const sortDelta = (Number(a.sortOrder) || Number.MAX_SAFE_INTEGER) - (Number(b.sortOrder) || Number.MAX_SAFE_INTEGER);
    if (sortDelta !== 0) return sortDelta;
    return sanitizeText(a.name).localeCompare(sanitizeText(b.name));
  });
}
